import { defineStore } from "pinia";
import axios from "axios";
import { login as loginApi } from "@/api/auth";
import {
  getMe as getMeApi,
  getMyPermissions as getMyPermissionsApi,
} from "@/api/common";
import { clearAuthTimers, scheduleRefresh } from "@/utils/request";
import { resetTabs } from "@/composables/useTabs";
import { ROLE } from "@/constants";
import router from "@/router";

// 解析 JWT 载荷（中间段 base64url 解码），失败返回空对象
const parseJwt = (token) => {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return {};
  }
};

export const useUserStore = defineStore("user", {
  state: () => ({
    token: "",
    refreshToken: "",
    expiresAt: 0,
    memberId: null,
    merchantId: null,
    memberName: "",
    merchantName: "",
    role: "",
    boss: false,
    authorities: [],
  }),

  getters: {
    isLoggedIn: (state) => !!state.token,
    isBoss: (state) => state.boss || state.role === ROLE.BOSS,
  },

  actions: {
    /** OAuth2 密码模式登录 */
    async login(loginForm) {
      const { username, password } = loginForm;
      const res = await loginApi({ username, password });
      this.token = res.data.access_token;
      this.refreshToken = res.data.refresh_token || "";
      const expiresIn = Number(res.data.expires_in) || 0;
      this.expiresAt = expiresIn > 0 ? Date.now() + expiresIn * 1000 : 0;
      try {
        await this.getUserInfo();
        await this.fetchPermissions();
      } catch {
        this.fillUserInfoFromJwt();
      }
      return res;
    },

    setToken(access_token, refresh_token, expiresAt) {
      this.token = access_token;
      this.refreshToken = refresh_token || this.refreshToken;
      if (typeof expiresAt === "number") this.expiresAt = expiresAt;
    },

    /**
     * 切换店铺成功：落地后端换发的令牌 + 目标店铺身份快照（POST /v1/shops/{id}/switch）
     * 只信任接口返回的身份，不再额外拉 /me；刷新令牌继承新 merchant_id，续期后不会退回原店铺。
     */
    applyShopSwitch(vo = {}) {
      if (!vo.accessToken) {
        throw new Error("切换失败：后端未返回新令牌");
      }
      const exp = Number(parseJwt(vo.accessToken).exp) || 0;
      this.token = vo.accessToken;
      this.refreshToken = vo.refreshToken || this.refreshToken;
      this.expiresAt = exp > 0 ? exp * 1000 : 0;
      // 身份快照：店铺 / 账号 / 角色 / 菜单权限
      if (vo.shopId !== undefined && vo.shopId !== null) {
        this.merchantId = vo.shopId;
      }
      if (vo.shopName) this.merchantName = vo.shopName;
      if (vo.accountNo) this.memberId = Number(vo.accountNo) || this.memberId;
      if (vo.roleCode) {
        this.role = vo.roleCode;
        this.boss = vo.roleCode === ROLE.BOSS;
      }
      if (Array.isArray(vo.menuCodes)) this.authorities = vo.menuCodes;
      // 换发后按新 accessToken 的 exp 重排续期
      scheduleRefresh(this.expiresAt);
    },

    /** 从 /me/permissions 拉权限码清单并写入 store */
    async fetchPermissions() {
      try {
        const res = await getMyPermissionsApi();
        const list = res.data;
        this.authorities = Array.isArray(list) ? list : [];
      } catch {
        // 拉失败时保留 JWT 里的 authorities 作为兜底
      }
    },

    /** 从 JWT 声明兜底填充用户信息（/me 接口不可用或字段缺失时） */
    fillUserInfoFromJwt() {
      const jwt = parseJwt(this.token);
      this.memberId = this.memberId ?? jwt.member_id ?? null;
      this.merchantId = this.merchantId ?? jwt.merchant_id ?? null;
      this.memberName = this.memberName || jwt.member_name || "";
      this.merchantName = this.merchantName || jwt.merchant_name || "";
      this.role = this.role || jwt.role || "";
      this.boss = this.boss || this.role === ROLE.BOSS;
      this.authorities = jwt.authorities || [];
    },

    /** 获取当前用户信息 */
    async getUserInfo() {
      const res = await getMeApi();
      const data = res.data || {};
      const jwt = parseJwt(this.token);
      this.memberId = data.memberId ?? jwt.member_id ?? null;
      this.merchantId = data.merchantId ?? jwt.merchant_id ?? null;
      this.memberName =
        data.memberName || data.nickName || jwt.member_name || "";
      this.merchantName = data.merchantName || jwt.merchant_name || "";
      this.role = data.role || jwt.role || "";
      this.boss = data.boss ?? this.role === ROLE.BOSS;
      this.authorities = data.authorities || [];
      return res;
    },

    /** 退出登录 */
    async logout() {
      try {
        await this.revokeToken();
      } catch (e) {
        // 即使后端接口失败也要清空本地状态
      }
      clearAuthTimers();
      this.resetState();
      resetTabs();
      router.push("/login");
    },

    /** OAuth2 撤销当前 access_token（revoke 端点未公开时静默失败） */
    async revokeToken() {
      if (!this.token) return;
      try {
        await axios.post("/oauth2/revoke", new URLSearchParams({}), {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${this.token}`,
          },
        });
      } catch {
        // revoke 失败不阻塞本地退出
      }
    },

    resetState() {
      this.token = "";
      this.refreshToken = "";
      this.expiresAt = 0;
      this.memberId = null;
      this.merchantId = null;
      this.memberName = "";
      this.merchantName = "";
      this.role = "";
      this.boss = false;
      this.authorities = [];
    },
  },

  persist: {
    key: "ezrent-user",
    storage: localStorage,
    pick: [
      "token",
      "refreshToken",
      "expiresAt",
      "memberId",
      "merchantId",
      "memberName",
      "merchantName",
      "role",
      "boss",
      "authorities",
    ],
  },
});
